import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const products = [
  { title: 'Lead Generation', desc: 'Przyciąga uwagę potencjalnych klientów i zwiększa sprzedaż', image: '/images/products/lead-gen.webp', href: '/uslugi#lead-gen' },
  { title: 'AI Voice Agent 24/7', desc: 'Wirtualny asystent, który odbiera za Ciebie telefony', image: '/images/products/voice-agent.webp', href: '/uslugi#voice-agent' },
  { title: 'Customer Support RAG', desc: 'Automatyczna obsługa klienta, która zna Twoją firmę', image: '/images/products/support-rag.webp', href: '/uslugi#rag' },
  { title: 'SMS Reaktywacja', desc: 'Przypomina o Tobie i sprowadza z powrotem starych klientów', image: '/images/products/sms-reaktywacja.webp', href: '/uslugi#sms' },
  { title: 'AIOs Multi-Agent', desc: 'Wirtualni pracownicy, którzy pracują dla Ciebie 24/7', image: '/images/products/aios-multi.webp', href: '/uslugi#aios' },
  { title: 'Workflow Automation', desc: 'Łączy Twoje narzędzia i wykonuje rutynę za Ciebie', image: '/images/products/n8n-workflow.webp', href: '/uslugi#workflow' }
];

export default function Explorations() {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const col1Ref = useRef(null);
  const col2Ref = useRef(null);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    // Dynamic import - GSAP + ScrollTrigger laduja sie tylko gdy ten komponent renderuje sie.
    // Nie wchodzi do initial bundle, oszczednosc ~27KB na pierwszy load.
    Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger')
    ]).then(([gsapMod, scrollTriggerMod]) => {
      if (cancelled) return;
      const gsap = gsapMod.gsap;
      const ScrollTrigger = scrollTriggerMod.ScrollTrigger;
      gsap.registerPlugin(ScrollTrigger);

      let mm = gsap.matchMedia();
      const ctx = gsap.context(() => {
        mm.add("(min-width: 768px)", () => {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top top",
              end: "+=1900",
              pin: true,
              anticipatePin: 1,
              scrub: 1,
              invalidateOnRefresh: true,
            }
          });

          // Karty startuja juz czesciowo widoczne i wylatuja gora rowno z koncem pinu
          // (wezszy zakres = brak pustych martwych klatek na wejsciu/wyjsciu).
          tl.fromTo(col1Ref.current,
            { y: "55vh" },
            { y: "-105vh", ease: "none" },
            0
          );

          tl.fromTo(col2Ref.current,
            { y: "70vh" },
            { y: "-105vh", ease: "none" },
            0
          );
        });
      }, containerRef);

      // Pozycje pinu przelicz po pelnym zaladowaniu strony: fonty + lazy obrazki w sekcjach
      // wyzej zmieniaja wysokosc layoutu juz po inicjalizacji ScrollTrigger, przez co start/end
      // sa nieaktualne i scroll "przeskakuje" przy wejsciu w sekcje. refresh() to koryguje.
      const refresh = () => ScrollTrigger.refresh();
      if (document.readyState === "complete") refresh();
      else window.addEventListener("load", refresh);
      const settleTimer = setTimeout(refresh, 1200);

      cleanup = () => {
        window.removeEventListener("load", refresh);
        clearTimeout(settleTimer);
        mm.revert();
        ctx.revert();
      };
    });

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative md:h-screen bg-bg overflow-hidden flex flex-col md:block py-20 md:py-0"
      id="produkty"
      aria-label="Produkty i usługi"
    >
      {/* Center Text */}
      <div
        ref={contentRef}
        className="relative md:absolute md:inset-0 md:h-screen z-10 flex flex-col items-center justify-center text-center px-4 md:px-0 mb-12 md:mb-0 pointer-events-none"
      >
        <div className="bg-bg/60 backdrop-blur-3xl px-6 md:px-12 py-8 md:py-10 rounded-[2rem] md:rounded-[3rem] border border-stroke/40 pointer-events-auto flex flex-col items-center w-full max-w-2xl mx-auto shadow-sm">
          <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-6">
            <div className="w-4 md:w-8 h-px bg-stroke"></div>
            <span className="text-[10px] md:text-xs text-muted uppercase tracking-[0.2em] md:tracking-[0.3em]">Co konkretnie wdrażam</span>
            <div className="w-4 md:w-8 h-px bg-stroke"></div>
          </div>

          <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tight font-light text-text-primary mb-4 md:mb-6 text-balance leading-[1.1]">
            6 produktów <span className="font-display italic block md:inline mt-1 md:mt-0">które działają</span>
          </h2>

          <p className="text-muted text-sm md:text-lg mb-6 md:mb-8 max-w-sm md:max-w-md mx-auto text-balance">
            Lead gen, voice agents, RAG, SMS, AIOs, workflow automation. Każdy produkt = realny case w produkcji.
          </p>

          <Link
            to="/uslugi"
            className="border-gradient inline-flex items-center justify-center gap-2 md:gap-3 bg-surface text-text-primary rounded-full px-6 py-3 md:px-8 md:py-4 hover:bg-bg transition-colors group w-full sm:w-auto"
          >
            <span className="relative z-10 font-medium text-sm md:text-base">Zobacz pełen katalog</span>
            <ArrowRight className="relative z-10 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Grid Columns */}
      <div className="relative md:absolute md:inset-0 w-full max-w-[1400px] mx-auto px-4 md:pointer-events-none">
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-12 lg:gap-40 h-full relative">

          {/* Column 1 */}
          <div ref={col1Ref} className="flex flex-col gap-6 md:gap-12 lg:gap-40 md:pointer-events-auto items-center md:items-start md:mt-0">
            {products.slice(0, 3).map((product, idx) => (
              <Link
                key={`col1-${idx}`}
                to={product.href}
                aria-label={product.title}
                className="w-full aspect-square max-w-[280px] sm:max-w-[320px] rounded-3xl overflow-hidden border border-stroke hover:border-text-primary/40 transition-colors cursor-pointer group md:hover:rotate-2 transform duration-500 shadow-lg md:shadow-2xl relative bg-surface"
              >
                <img
                  src={product.image}
                  alt={`${product.title} - ${product.desc}`}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 md:group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg/90 via-bg/40 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex flex-col justify-end p-5 text-left">
                  <h3 className="text-text-primary font-medium text-base md:text-lg mb-1">{product.title}</h3>
                  <p className="text-muted text-xs md:text-sm">{product.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Column 2 */}
          <div ref={col2Ref} className="flex flex-col gap-6 md:gap-12 lg:gap-40 md:pointer-events-auto items-center md:items-end">
            {products.slice(3, 6).map((product, idx) => (
              <Link
                key={`col2-${idx}`}
                to={product.href}
                aria-label={product.title}
                className="w-full aspect-square max-w-[280px] sm:max-w-[320px] rounded-3xl overflow-hidden border border-stroke hover:border-text-primary/40 transition-colors cursor-pointer group md:hover:-rotate-2 transform duration-500 shadow-lg md:shadow-2xl relative bg-surface"
              >
                <img
                  src={product.image}
                  alt={`${product.title} - ${product.desc}`}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 md:group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg/90 via-bg/40 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex flex-col justify-end p-5 text-left">
                  <h3 className="text-text-primary font-medium text-base md:text-lg mb-1">{product.title}</h3>
                  <p className="text-muted text-xs md:text-sm">{product.desc}</p>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
